import _ from "lodash";
import React from "react";

type UseDebounceOptions = {
    // Run a still-pending call immediately on unmount instead of dropping it. Use this for autosave-style
    // debounces where leaving the page right after an edit must not silently discard it; leave the default
    // (cancel) for input debounces, where firing after the field is gone serves no purpose.
    flushOnUnmount?: boolean;
    // Passed straight through to lodash's debounce. Defaults match lodash's own default (trailing-edge only:
    // wait for `delay` of silence, then fire once with the latest args). Pass `{ leading: true, trailing:
    // false }` for the opposite shape — fire on the first call, then ignore repeats for `delay` — which suits
    // something like a barcode scanner emitting many detections a second, where waiting for silence would add
    // a full `delay` of dead time before the first hit is acted on.
    leading?: boolean;
    trailing?: boolean;
};

// The debounced function returned below has to stay the same instance across renders, or the timer would be reset
// every time the caller hands in a freshly built callback, which defeats the debounce. `useEffectEvent` gives the
// memoized instance a stable handle that still calls into the current render's closure.
const useDebounce = <Args extends unknown[]>(callback: (...args: Args) => void, delay: number, options?: UseDebounceOptions) => {
    const onInvoke = React.useEffectEvent(callback);

    const { flushOnUnmount = false, leading = false, trailing = true } = options ?? {};
    const debounced = React.useMemo(() => _.debounce(onInvoke, delay, { leading, trailing }), [delay, leading, trailing]);

    React.useEffect(() => {
        return () => (flushOnUnmount ? debounced.flush() : debounced.cancel());
    }, [debounced, flushOnUnmount]);

    return debounced;
};

export default useDebounce;
